// Org-wide CRM files table.
import { useEffect, useState } from 'react';
import { Badge, BaseTable } from '@/components/ui';
import type { BaseTableColumn, BadgeTone } from '@/components/ui';
import { organizationService } from '@/services/api';
import { useOrgPageMeta, useOrgSlug } from '@/layouts/orgSettingsMeta';
import type { CrmFile } from '@/types';
import OrgSectionHeading from '@/features/organization/OrgSectionHeading';

function statusTone(status: string): BadgeTone {
  const normalized = status.toLowerCase();
  if (normalized === 'completed' || normalized === 'ready' || normalized === 'done') {
    return 'success';
  }
  if (normalized === 'processing' || normalized === 'pending') return 'warning';
  if (normalized === 'failed' || normalized === 'error') return 'danger';
  return 'secondary';
}

function isCompleteStatus(status: string): boolean {
  const normalized = status.toLowerCase();
  return normalized === 'completed' || normalized === 'ready' || normalized === 'done';
}

const columns: BaseTableColumn<CrmFile>[] = [
  { key: 'title', header: 'Title', render: (f) => f.title || f.processId },
  { key: 'createdAt', header: 'Created' },
  {
    key: 'status',
    header: 'Status',
    render: (f) => <Badge tone={statusTone(f.status)}>{f.status}</Badge>,
  },
];

export default function CrmPage() {
  const slug = useOrgSlug();
  useOrgPageMeta('CRM', <>Organization-wide CRM files for this organization.</>);

  const [files, setFiles] = useState<CrmFile[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    organizationService
      .getCrm(slug)
      .then((data) => {
        if (active) setFiles(data.files);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [slug]);

  return (
    <div className="org-panel">
      <OrgSectionHeading plain title="Files" />
      <BaseTable
        className="org-crm-table"
        borderless
        columns={columns}
        rows={files}
        loading={loading}
        emptyMessage="No files yet."
        getRowKey={(f) => f.processId}
        rowClass={(f) => (isCompleteStatus(f.status) ? 'org-crm-row--done' : undefined)}
      />
    </div>
  );
}
